import React, { useCallback, useEffect, useState } from 'react';
import Colors from '../../constants/colors';
import { DESKTOP_CONFIG, DOCK_DEFAULT_ICON_SIZE, DOCK_ICON_SIZES } from '../../constants/desktopConfig';
import ShowcaseExplorer from '../applications/ShowcaseExplorer';
import PdfViewer from '../applications/PdfViewer';
import Doom from '../applications/Doom';
import ShutdownSequence from './ShutdownSequence';
import Toolbar from './Toolbar';
import MenuBar from './MenuBar';
import DesktopShortcut, { DesktopShortcutProps } from './DesktopShortcut';
import Mario from '../applications/Mario';
import Pacman from '../applications/Pacman';
import Quake from '../applications/Quake';
import Tetris from '../applications/Tetris';
import Tron from '../applications/Tron';
import { IconName } from '../../assets/icons';
import Credits from '../applications/Credits';
import CursorAppWindow from '../applications/CursorAppWindow';
import TerminalApp from '../applications/TerminalApp';
import Bin from '../applications/Bin';
import Health from '../applications/Health';
import MusicApp from '../applications/MusicApp';

export interface DesktopProps {
    isInIframe?: boolean;
}

type ExtendedWindowAppProps<T> = T & WindowAppProps;


/** Dock order: apps, then games, then separator |, then Bin (dock only, like Apple). */
const DOCK_ORDER = ['linkedin', 'calendar', 'github', 'email', 'cursor', 'safari', 'music', 'terminal', 'health', 'credits', 'doom', 'mario', 'pacman', 'quake', 'tetris', 'tron', 'separator', 'bin'];

/** App keys that are games (site background music is auto-muted when any is open). */
const GAME_KEYS = ['doom', 'mario', 'pacman', 'quake', 'tetris', 'tron'];

/** All apps: dock apps + games + credits (desktop). Dock icon sizes: see `DOCK_ICON_SIZES` in desktopConfig. */
const APPLICATIONS: {
    [key in string]: {
        key: string;
        name: string;
        shortcutIcon: IconName;
        iconStyle: 'apple' | 'retro';
        component?: React.FC<ExtendedWindowAppProps<any>>;
        externalUrl?: string;
        mailto?: string;
    };
} = {
    safari: {
        key: 'safari',
        name: 'Safari',
        shortcutIcon: 'safariIcon',
        iconStyle: 'apple',
        component: ShowcaseExplorer,
    },
    music: {
        key: 'music',
        name: 'Music',
        shortcutIcon: 'appleMusicIcon',
        iconStyle: 'apple',
        component: MusicApp,
    },
    calendar: {
        key: 'calendar',
        name: 'Calendar',
        shortcutIcon: 'calendarIcon',
        iconStyle: 'apple',
        externalUrl: 'https://calendly.com/f-juzilins/30min',
    },
    email: {
        key: 'email',
        name: 'Mail',
        shortcutIcon: 'mailIcon',
        iconStyle: 'apple',
        mailto: 'mailto:f.juzilins@gmail.com',
    },
    linkedin: {
        key: 'linkedin',
        name: 'LinkedIn',
        shortcutIcon: 'linkedinIcon',
        iconStyle: 'apple',
        externalUrl: 'https://www.linkedin.com/in/philippju/',
    },
    github: {
        key: 'github',
        name: 'GitHub',
        shortcutIcon: 'githubIcon',
        iconStyle: 'apple',
        externalUrl: 'https://github.com/MrOgurchik3',
    },
    cursor: {
        key: 'cursor',
        name: 'Cursor',
        shortcutIcon: 'cursorIcon',
        iconStyle: 'apple',
        component: CursorAppWindow,
    },
    terminal: {
        key: 'terminal',
        name: 'Terminal',
        shortcutIcon: 'terminalIcon',
        iconStyle: 'apple',
        component: TerminalApp,
    },
    health: {
        key: 'health',
        name: 'Health',
        shortcutIcon: 'healthIcon',
        iconStyle: 'apple',
        component: Health,
    },
    bin: {
        key: 'bin',
        name: 'Bin',
        shortcutIcon: 'binIcon',
        iconStyle: 'apple',
        component: Bin,
    },
    doom: {
        key: 'doom',
        name: 'Doom',
        shortcutIcon: 'doomIcon',
        iconStyle: 'retro',
        component: Doom,
    },
    mario: {
        key: 'mario',
        name: 'Mario',
        shortcutIcon: 'marioIcon',
        iconStyle: 'retro',
        component: Mario,
    },
    pacman: {
        key: 'pacman',
        name: 'Pac-Man',
        shortcutIcon: 'pacmanIcon',
        iconStyle: 'retro',
        component: Pacman,
    },
    quake: {
        key: 'quake',
        name: 'Quake',
        shortcutIcon: 'quakeIcon',
        iconStyle: 'retro',
        component: Quake,
    },
    tetris: {
        key: 'tetris',
        name: 'Tetris',
        shortcutIcon: 'tetrisIcon',
        iconStyle: 'retro',
        component: Tetris,
    },
    tron: {
        key: 'tron',
        name: 'Tron: Light Cycles',
        shortcutIcon: 'tronIcon',
        iconStyle: 'retro',
        component: Tron,
    },
    credits: {
        key: 'credits',
        name: 'Credits',
        shortcutIcon: 'credits',
        iconStyle: 'apple',
        component: Credits,
    },
};

const Desktop: React.FC<DesktopProps> = ({ isInIframe }) => {
    const [windows, setWindows] = useState<DesktopWindows>({});

    const [shortcuts, setShortcuts] = useState<DesktopShortcutProps[]>([]);

    const [shutdown, setShutdown] = useState(false);

    /** User toggled "Mute site music" (only relevant when embedded in iframe). */
    const [userMutedSiteMusic, setUserMutedSiteMusic] = useState(false);
    const [numShutdowns, setNumShutdowns] = useState(1);

    const hasGameOpen = Object.keys(windows).some((k) => GAME_KEYS.includes(k));
    const siteMusicMuted = hasGameOpen || userMutedSiteMusic;

    useEffect(() => {
        if (!isInIframe || typeof window.parent?.postMessage !== 'function') return;
        window.parent.postMessage({ type: 'mute-background-music', mute: siteMusicMuted }, '*');
    }, [isInIframe, siteMusicMuted]);

    useEffect(() => {
        if (shutdown === true) {
            rebootDesktop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shutdown]);

    /** Desktop: Safari, Credits, CV. Bin is dock-only. */
    const DESKTOP_SHORTCUT_ORDER = ['safari', 'credits', 'portfolio_cv'];

    useEffect(() => {
        const newShortcuts: DesktopShortcutProps[] = [];
        DESKTOP_SHORTCUT_ORDER.forEach((key) => {
            if (key === 'portfolio_cv') {
                newShortcuts.push({
                    shortcutName: 'CV',
                    icon: 'pdfIcon',
                    iconStyle: 'apple',
                    iconSize: 72,
                    onOpen: () =>
                        addWindow(
                            'portfolio_cv',
                            <PdfViewer
                                onClose={() => removeWindow('portfolio_cv')}
                                onInteract={() => onWindowInteract('portfolio_cv')}
                                onMinimize={() => minimizeWindow('portfolio_cv')}
                            />,
                            { name: 'Filips Juzilins - CV', icon: 'pdfIcon' }
                        ),
                });
                return;
            }
            const app = APPLICATIONS[key];
            if (!app) return;
            newShortcuts.push({
                shortcutName: app.name,
                icon: app.shortcutIcon,
                iconStyle: app.iconStyle,
                iconSize: key === 'credits' ? 68 : undefined,
                onOpen: () => openApp(key),
            });
        });

        if (isInIframe) {
            openApp('music');
        }
        newShortcuts.forEach((shortcut) => {
            if (shortcut.shortcutName === 'Safari') {
                shortcut.onOpen();
            }
        });

        setShortcuts(newShortcuts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rebootDesktop = useCallback(() => {
        setWindows({});
    }, []);

    const removeWindow = useCallback((key: string) => {
        // Absolute hack and a half
        setTimeout(() => {
            setWindows((prevWindows) => {
                const newWindows = { ...prevWindows };
                delete newWindows[key];
                return newWindows;
            });
        }, 100);
    }, []);

    const minimizeWindow = useCallback((key: string) => {
        setWindows((prevWindows) => {
            const newWindows = { ...prevWindows };
            newWindows[key].minimized = true;
            return newWindows;
        });
    }, []);

    const getHighestZIndex = useCallback((): number => {
        let highestZIndex = 0;
        Object.keys(windows).forEach((key) => {
            const window = windows[key];
            if (window) {
                if (window.zIndex > highestZIndex)
                    highestZIndex = window.zIndex;
            }
        });
        return highestZIndex;
    }, [windows]);

    const toggleMinimize = useCallback(
        (key: string) => {
            const newWindows = { ...windows };
            const highestIndex = getHighestZIndex();
            if (
                newWindows[key].minimized ||
                newWindows[key].zIndex === highestIndex
            ) {
                newWindows[key].minimized = !newWindows[key].minimized;
            }
            newWindows[key].zIndex = getHighestZIndex() + 1;
            setWindows(newWindows);
        },
        [windows, getHighestZIndex]
    );

    const onWindowInteract = useCallback(
        (key: string) => {
            setWindows((prevWindows) => ({
                ...prevWindows,
                [key]: {
                    ...prevWindows[key],
                    zIndex: 1 + getHighestZIndex(),
                },
            }));
        },
        [setWindows, getHighestZIndex]
    );

    const startShutdown = useCallback(() => {
        setTimeout(() => {
            setShutdown(true);
            setNumShutdowns(numShutdowns + 1);
        }, 600);
    }, [numShutdowns]);

    const addWindow = useCallback(
        (key: string, element: JSX.Element, options?: { name: string; icon: IconName }) => {
            const app = APPLICATIONS[key];
            setWindows((prevState) => ({
                ...prevState,
                [key]: {
                    zIndex: getHighestZIndex() + 1,
                    minimized: false,
                    component: element,
                    name: options?.name ?? app?.name ?? key,
                    icon: options?.icon ?? app?.shortcutIcon ?? 'windowExplorerIcon',
                },
            }));
        },
        [getHighestZIndex]
    );

    const openApp = useCallback(
        (key: string) => {
            const app = APPLICATIONS[key];
            if (!app) return;
            if (app.externalUrl) {
                window.open(app.externalUrl, '_blank');
                return;
            }
            if (app.mailto) {
                window.location.href = app.mailto;
                return;
            }
            if (windows[key]) {
                toggleMinimize(key);
                return;
            }
            if (app.component) {
                addWindow(
                    key,
                    <app.component
                        onInteract={() => onWindowInteract(key)}
                        onMinimize={() => minimizeWindow(key)}
                        onClose={() => removeWindow(key)}
                        key={key}
                    />
                );
            }
        },
        [windows, addWindow, toggleMinimize, onWindowInteract, minimizeWindow, removeWindow]
    );

    const dockApps = DOCK_ORDER.map((key) => {
        if (key === 'separator') {
            return { key: '__separator__', name: '', icon: 'windowExplorerIcon' as IconName, iconStyle: 'apple' as const, isSeparator: true };
        }
        const app = APPLICATIONS[key];
        const iconSize =
            DOCK_ICON_SIZES[key] ?? DOCK_DEFAULT_ICON_SIZE;
        return app
            ? {
                  key: app.key,
                  name: app.name,
                  icon: app.shortcutIcon,
                  iconStyle: app.iconStyle,
                  iconSize,
                  isSeparator: false,
              }
            : null;
    }).filter(Boolean) as { key: string; name: string; icon: IconName; iconStyle: 'apple' | 'retro'; iconSize?: number; isSeparator?: boolean }[];

    const desktopStyle = Object.assign(
        {},
        styles.desktop,
        DESKTOP_CONFIG.wallpaperImage && {
            backgroundImage: `url(${DESKTOP_CONFIG.wallpaperImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        }
    );

    return !shutdown ? (
        <div style={desktopStyle}>
            {isInIframe && (
                <div style={styles.cursorEasterEgg} title="Cursor">
                    Don't delete the terminal.
                </div>
            )}
            <MenuBar shutdown={startShutdown} />
            {Object.keys(windows).map((key) => {
                const element = windows[key].component;
                if (!element) return <div key={`win-${key}`}></div>;
                return (
                    <div
                        key={`win-${key}`}
                        style={Object.assign(
                            {},
                            { zIndex: windows[key].zIndex },
                            windows[key].minimized && styles.minimized
                        )}
                    >
                        {React.cloneElement(element, {
                            key,
                            onInteract: () => onWindowInteract(key),
                            onClose: () => removeWindow(key),
                            ...(key === 'music'
                                ? {
                                      siteMusicPlaying: !siteMusicMuted,
                                      gameBlocksPlayback: hasGameOpen,
                                      onTogglePlayPause: () =>
                                          setUserMutedSiteMusic((m) => !m),
                                  }
                                : {}),
                        })}
                    </div>
                );
            })}
            <div style={styles.shortcuts}>
                {shortcuts.map((shortcut, i) => (
                    <div style={styles.shortcutContainer} key={shortcut.shortcutName}>
                        <DesktopShortcut
                            icon={shortcut.icon}
                            shortcutName={shortcut.shortcutName}
                            onOpen={shortcut.onOpen}
                            iconStyle={shortcut.iconStyle}
                            iconSize={shortcut.iconSize}
                        />
                    </div>
                ))}
            </div>
            <Toolbar
                dockApps={dockApps}
                windows={windows}
                onOpenApp={openApp}
            />
        </div>
    ) : (
        <ShutdownSequence
            setShutdown={setShutdown}
            numShutdowns={numShutdowns}
        />
    );
};

const MENU_BAR_HEIGHT = DESKTOP_CONFIG.menuBarHeight;

const styles: StyleSheetCSS = {
    desktop: {
        minHeight: '100%',
        flex: 1,
        backgroundColor: Colors.gray1,
    },
    shutdown: {
        minHeight: '100%',
        flex: 1,
        backgroundColor: '#1d2e2f',
    },
    shortcutContainer: {
        position: 'relative',
        width: 80,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    shortcuts: {
        position: 'absolute',
        top: MENU_BAR_HEIGHT + 20,
        right: 28,
        left: 'auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 80px)',
        gap: '24px 22px',
        alignContent: 'start',
        justifyItems: 'center',
    },
    minimized: {
        pointerEvents: 'none',
        opacity: 0,
    },
    cursorEasterEgg: {
        position: 'fixed',
        bottom: 12,
        left: 12,
        fontSize: 10,
        color: 'rgba(255,255,255,0.4)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        pointerEvents: 'none',
    },
};

export default Desktop;
